import { clsx } from "clsx";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { IconCaretDownFilled, IconCircleCheckFilled } from "@tabler/icons-react";
import {
  parsePhoneNumberFromString,
  getExampleNumber,
  type CountryCode,
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.full.json";
import examples from "libphonenumber-js/mobile/examples";
import { Input } from "./input";
import { Text } from "../typography";
import { DEFAULT_PHONE_COUNTRIES, type PhoneCountry } from "./phone-countries";

function flagImg(iso2: string) {
  return `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`;
}

function getCountryLengths(iso2: string): { min: number; max: number } {
  const entry = (metadata as Record<string, unknown>).countries as Record<
    string,
    unknown[]
  >;
  const data = entry?.[iso2];
  const lengths = data?.[3] as number[] | undefined;
  if (lengths && lengths.length > 0) {
    return { min: Math.min(...lengths), max: Math.max(...lengths) };
  }
  const example = getExampleNumber(iso2 as CountryCode, examples);
  const len = example?.nationalNumber.length ?? 15;
  return { min: len, max: len };
}

export type PhoneInputProps = {
  label?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  /** E.164 phone number (dial code + national number, e.g. "+97412345678"). */
  value: string;
  onChange: (value: string) => void;
  countries?: readonly PhoneCountry[];
  /** ISO2 used when the value is empty. Defaults to "QA". */
  defaultCountry?: string;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
};

function matchCountry(
  value: string,
  countries: readonly PhoneCountry[],
): PhoneCountry | undefined {
  if (!value.startsWith("+")) return undefined;
  return countries
    .slice()
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((c) => value.startsWith(c.dialCode));
}

function validatePhone(e164: string, iso2: string): boolean {
  const parsed = parsePhoneNumberFromString(e164, iso2 as CountryCode);
  return parsed?.isValid() === true;
}

function getPlaceholder(iso2: string): string {
  const example = getExampleNumber(iso2 as CountryCode, examples);
  if (!example) return "";
  return example.formatNational();
}

export function PhoneInput({
  label = "Phone number",
  required,
  error,
  helperText,
  value,
  onChange,
  countries = DEFAULT_PHONE_COUNTRIES,
  defaultCountry = "QA",
  placeholder,
  disabled,
  name,
}: PhoneInputProps) {
  const detected = useMemo(
    () => matchCountry(value, countries),
    [value, countries],
  );
  const fallback =
    countries.find((c) => c.iso2 === defaultCountry) ?? countries[0];
  const [country, setCountry] = useState<PhoneCountry>(detected ?? fallback);
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const { min: minLen, max: maxLen } = useMemo(
    () => getCountryLengths(country.iso2),
    [country.iso2],
  );

  useEffect(() => {
    if (detected && detected.iso2 !== country.iso2) {
      setCountry(detected);
    }
  }, [detected, country.iso2]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const national = detected
    ? value.slice(detected.dialCode.length).replace(/^\s+/, "")
    : value.replace(/^\+/, "");
  const nationalDigits = national.replace(/\D/g, "");
  const e164 = `${country.dialCode}${nationalDigits}`;
  const valid = nationalDigits.length > 0 && validatePhone(e164, country.iso2);
  const dynamicPlaceholder = placeholder ?? getPlaceholder(country.iso2);

  const showFormatError =
    !error &&
    touched &&
    nationalDigits.length >= minLen &&
    !valid;

  const handleNationalChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    const capped = digits.slice(0, maxLen);
    if (!touched && capped.length > 0) setTouched(true);
    onChange(capped ? `${country.dialCode}${capped}` : "");
  };

  const selectCountry = (next: PhoneCountry) => {
    setCountry(next);
    setOpen(false);
    const nextMax = getCountryLengths(next.iso2).max;
    const capped = nationalDigits.slice(0, nextMax);
    onChange(capped ? `${next.dialCode}${capped}` : "");
  };

  const leftAddon = (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "flex items-center gap-1.5 rounded-md outline-none",
          "disabled:cursor-not-allowed",
        )}
      >
        <img src={flagImg(country.iso2)} alt={country.iso2} className="h-3.5 w-5 rounded-sm object-cover" />
        <IconCaretDownFilled size={14} className="text-textBody" />
      </button>
      {open ? (
        <ul
          id={listboxId}
          role="listbox"
          className={clsx(
            "absolute left-0 top-full z-20 mt-2 max-h-64 w-64 overflow-auto",
            "rounded-xl border border-borderDefault bg-bgWhite shadow-lg",
            "py-1",
          )}
        >
          {countries.map((c) => {
            const selected = c.iso2 === country.iso2;
            return (
              <li key={c.iso2} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => selectCountry(c)}
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left",
                    "hover:bg-bgMuted",
                    selected && "bg-bgMuted",
                  )}
                >
                  <img src={flagImg(c.iso2)} alt={c.iso2} className="h-3.5 w-5 shrink-0 rounded-sm object-cover" />
                  <Text variant="bodySm" tone="dark" inline className="flex-1">
                    {c.name}
                  </Text>
                  <Text variant="bodySm" tone="muted" inline>
                    {c.dialCode}
                  </Text>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );

  return (
    <Input
      name={name}
      type="tel"
      inputMode="tel"
      label={label}
      required={required}
      error={error ?? (showFormatError ? `Invalid phone number for ${country.name}` : undefined)}
      helperText={helperText}
      disabled={disabled}
      placeholder={dynamicPlaceholder}
      maxLength={maxLen}
      leftAddon={leftAddon}
      rightIcon={
        valid && !disabled ? (
          <IconCircleCheckFilled size={20} className="text-accentGreen" />
        ) : undefined
      }
      value={national}
      onChange={handleNationalChange}
      onBlur={() => {
        if (nationalDigits.length > 0) setTouched(true);
      }}
    />
  );
}

export default PhoneInput;
